import React, { FunctionComponent, useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import { Button, CircularLoader, Dropdown, isMobileDevice, Pill } from 'rs-emd-ui-atoms';
import { DTPStockBadge, DTPStockInfo, EnergyLabelProductInfo } from '..';
import { getLabel } from '../../../helpers/html.utils';
import { isUnavailableProduct } from '../../../helpers/stock.utils';
import { includeVat } from '../../../helpers/user-settings.utils';
import { Validation } from '../../../helpers/validation.utils';
import { IPrice } from '../../../models/common/pricing/price.model';
import { StockStatus } from '../../../models/common/stock.model';
import { getPrimaryImageUrl } from '../../../models/pdp/pdp-media.model';
import { PPK } from '../../product-detail-page';
import Calibration from '../../product-detail-page/calibration/calibration-component';
import { Action, AddToBasketCTAActionType, PriceAreaProps, State } from './add-to-basket-cta-component-model';
import styles from './add-to-basket-cta-component.module.scss';

export const AddToBasketCTA: FunctionComponent<PriceAreaProps> = (props: PriceAreaProps) => {
  const [state, dispatch] = useReducer(reducer, {
    quantity: props.priceArea.minimumOrderQuantity,
    totalPriceIncVat: props.priceArea.prices[0].totalPrice,
    totalPriceExVat:
      props.priceArea.showExVat && props.priceArea.pricesExVat.length > 0 ? props.priceArea.pricesExVat[0].totalPrice : undefined,
    dualPriceIncVat: props.priceArea.priceDualCurrency ?? undefined,
    dualPriceExVat: props.priceArea.priceExVatDualCurrency ?? undefined,
    isValidQty: true,
    isBusy: false,
    selectedPriceBreakIndex: props.selectedPriceBreakIndex,
    defaultDropdownSelection: props.priceArea.prices[0].quantity,
    isDropdownOpen: false,
    lastPropPricesFirstPriceQty: props.priceArea.prices[0].quantity,
    lastPropPricesFirstTotalPrice: props.priceArea.prices[0].totalPrice,
    isCalibrationDropdownOpen: false,
    isPPKDropdownOpen: false,
    isCalibrationChanging: false,
  });

  let searchValueTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const [incVat, setIncVat] = useState<boolean>(false);
  const hasDiscount = props.priceArea.price?.priceAfter?.value !== props.priceArea.price?.priceBefore?.value;
  const showImportedByAddress = (props.labels?.filter((l) => l.name === 'imported_by_address') ?? []).length > 0;

  useLayoutEffect(() => {
    setIncVat(includeVat());
  }, []);

  //fix for: https://app.clickup.com/t/1n5vzxx?comment=608570417
  useLayoutEffect(() => {
    if (state.isDropdownOpen && isMobileDevice()) {
      document.body.classList.remove('no-scroll');
    }
  }, [state.isDropdownOpen]);

  //update default dropdown selection when prop product min qty changes or when changing ppk (and dropdown prices change)
  useEffect(() => {
    if (props.priceArea.prices[0].quantity !== state.lastPropPricesFirstPriceQty) {
      props.onQtyChange?.(props.priceArea.prices[0].quantity);
      dispatch({ type: AddToBasketCTAActionType.UpdateDefaultDropdownSelection });
    }
  }, [props.onQtyChange, props.priceArea.prices, state.lastPropPricesFirstPriceQty]);

  //get price for quantity not listed in dropdown values
  useEffect(() => {
    if (state.isSearchingUnlistedQuantity && props.titleArea?.stockCode && state.isValidQty) {
      const qty = state.quantity;

      //query to get price for quantity not listed in dropdown values then set price
      props.pricingService
        .getPricesByQty({
          stockCode: props.titleArea?.stockCode,
          quantity: qty,
        })
        .then((res) => {
          dispatch({
            type: AddToBasketCTAActionType.SetUnlistedQuantityPrice,
            qty: qty,
            price: res.priceArea ?? undefined,
            message: res.priceArea && res.priceArea.errorMessage.length > 0 ? res.priceArea?.errorMessage : undefined,
          });
        });
    }
  }, [props.pricingService, props.titleArea, state.isSearchingUnlistedQuantity, state.isValidQty, state.quantity]);

  useEffect(() => {
    dispatch({ type: AddToBasketCTAActionType.SetPriceBreakIndex, index: props.selectedPriceBreakIndex });
  }, [props.selectedPriceBreakIndex]);

  const onSearchValueChange = useCallback(
    (value: string) => {
      let numVal = Number(value);
      props.onQtyChange && props.onQtyChange(numVal);

      if (searchValueTimeoutRef.current) clearTimeout(searchValueTimeoutRef.current);

      //if unlisted quantity
      if (
        (incVat || !props.priceArea.showExVat ? props.priceArea.prices : props.priceArea.pricesExVat)
          .map((p) => p.quantity.toString())
          .filter((p) => p === value.toLowerCase()).length === 0
      ) {
        let validationRes = Validation.validateQuantity(props.priceArea.minimumOrderQuantity, props.priceArea.ssm, value);

        if (validationRes.isValid) {
          //throttle 300ms
          searchValueTimeoutRef.current = setTimeout(() => {
            dispatch({
              type: AddToBasketCTAActionType.SearchUnlistedQuantity,
              quantity: numVal,
            });
          }, 300);
        } else {
          dispatch({ type: AddToBasketCTAActionType.SetInvalidUnlistedQty, quantity: numVal, errorCode: validationRes.errorCode });
        }
      }
      //if listed quantity
      else {
        dispatch({
          type: AddToBasketCTAActionType.SearchListedQuantity,
          quantity: numVal,
        });
      }
    },
    [incVat, props]
  );

  //useCallback required to run only when state changes & when fn is called
  const getDropDownValues = useCallback(() => {
    const prices = incVat || !props.priceArea.showExVat ? props.priceArea.prices : props.priceArea.pricesExVat;

    if (props.priceArea.prices[0].quantity !== state.lastPropPricesFirstPriceQty) {
      return [
        {
          key: props.priceArea.prices[0].quantity,
          elt: (
            <div className={styles['qty-opt']}>
              <span>{props.priceArea.prices[0].quantity}</span>
              <span className={styles['qty-price']}>{props.priceArea.prices[0].totalPrice.priceAfter?.displayValue}</span>
            </div>
          ),
          searchableString: props.priceArea.prices[0].quantity.toString(),
        },
      ];
    }
    // if errormessage and qty is not a substring of listed value
    else if (
      !state.isValidQty &&
      prices.map((p) => p.quantity.toString()).filter((p) => p.includes(state.quantity.toString().toLowerCase())).length === 0
    )
      return [];
    if (!state.isSearchingUnlistedQuantity) {
      return prices.map((p) => {
        return {
          key: p.quantity,
          elt: (
            <div className={styles['qty-opt']}>
              <span>{p.quantity}</span>
              <span className={styles['qty-price']}>{p.totalPrice.priceAfter?.displayValue}</span>
            </div>
          ),
          searchableString: p.quantity.toString(),
        };
      });
    } else {
      return [
        {
          key: state.quantity,
          elt: (
            <div className={styles['qty-opt']}>
              <span>{state.quantity}</span>
              {state.unlistedQuantityPrice ? (
                <span className={styles['qty-price']}>
                  {
                    (state.unlistedQuantityPrice.pricesExVat[0].totalPrice ?? state.unlistedQuantityPrice.prices[0].totalPrice)!.priceAfter
                      ?.displayValue
                  }
                </span>
              ) : (
                <CircularLoader />
              )}
            </div>
          ),
          searchableString: state.quantity.toString(),
        },
      ];
    }
  }, [
    incVat,
    props.priceArea.prices,
    props.priceArea.pricesExVat,
    props.priceArea.showExVat,
    state.isSearchingUnlistedQuantity,
    state.isValidQty,
    state.lastPropPricesFirstPriceQty,
    state.quantity,
    state.unlistedQuantityPrice,
  ]);

  //update prices when calibration changed (may require unlisted qty query to maintain current selected qty)
  useEffect(() => {
    if (state.isCalibrationChanging && props.priceArea.prices[0]?.totalPrice !== state.lastPropPricesFirstTotalPrice) {
      dispatch({ type: AddToBasketCTAActionType.CalibrationChange, isChanging: false });

      onSearchValueChange(state.quantity.toString());
    }
  }, [onSearchValueChange, props.priceArea.prices, state.isCalibrationChanging, state.lastPropPricesFirstTotalPrice, state.quantity]);

  let isUnavailableProductFlag = isUnavailableProduct(props.stockArea?.statusCode);

  let stock = props.stockArea?.isUsingDTP
    ? props.isInFixedCTA && (
        <>
          <DTPStockBadge labels={props.labels} stockArea={props.stockArea} />
          {isUnavailableProductFlag && <DTPStockInfo labels={props.labels} stockArea={props.stockArea} />}
        </>
      )
    : ((props.stockArea?.title && props.stockArea?.statusCode !== StockStatus.LocallyManaged) ||
        (props.stockArea?.statusCode === StockStatus.Discontinued && props.stockArea.stockMessage)) && (
        <Pill
          class={`${styles.stock} stock ${props.isInFixedCTA ? styles['fixed-cta-stock'] : ''} ${
            props.stockArea?.statusCode.toLowerCase() ?? ''
          }`}>
          {props.stockArea?.title} {props.stockArea?.statusCode === StockStatus.Discontinued ? props.stockArea.stockMessage : ''}
        </Pill>
      );

  const energyArrows = props.energyArea?.energyLabel && (
    <EnergyLabelProductInfo
      labels={props.labels ?? undefined}
      energyArea={props.energyArea}
      stockCode={props.titleArea?.stockCode ?? ''}
      productService={props.productService}
      className={props.isInFixedCTA ? styles['energy-fixed-cta'] : ''}
      ratingLetterFontSize={props.isInFixedCTA ? 18 : 24}
      euElrProductInformationSheetUrl={props.euElrProductInformationSheet?.url}
      onModalToggleCallback={(isOpen: boolean) => props.onEnergyModalToggle && props.onEnergyModalToggle(isOpen)}
      onUnavailableEnergyProductInfoSheetBtnClick={props.onUnavailableEnergyProductInfoSheetBtnClick}
    />
  );

  // change x-index of header when dropdown is opened in fixed-cta
  useEffect(() => {
    document.documentElement.style.setProperty(
      '--is-fixed-cta-with-open-dropdown-header-z-index',
      props.isInFixedCTA === true && (state.isCalibrationDropdownOpen || state.isPPKDropdownOpen) ? '992' : '999'
    );
  }, [props.isInFixedCTA, state.isCalibrationDropdownOpen, state.isPPKDropdownOpen]);

  return (
    <div className={styles['add-to-basket-cta']}>
      {(!props.isInFixedCTA || isMobileDevice()) && stock}

      {(!props.stockArea?.isUsingDTP || !isUnavailableProductFlag) && (
        <>
          <div className={styles['price-details']} data-verbolia-id='add-to-basket-cta-price-details'>
            {hasDiscount && <p className={styles['your-price']}>{getLabel(props.labels, 'your_price')}</p>}
            {getPriceLine()}

            {/** Energy Arrows */}
            {props.isInFixedCTA && energyArrows}

            <div className={`snippet ${styles.title}`}>{props.titleArea?.title}</div>
          </div>

          {/** Energy Arrows */}
          {!props.isInFixedCTA && energyArrows}

          {/* if not permanently discontinued */}
          {!(props.stockArea?.statusCode === StockStatus.Discontinued && !props.stockArea.stockMessage) && !props.isBarred && (
            <>
              <div
                className={`${styles['cta-area-wrapper']} ${props.isInFixedCTA ? styles['fixed-cta-area-wrapper'] : ''}`}
                data-verbolia-id='add-to-basket-cta-area-wrapper'>
                {props.isInFixedCTA && !isMobileDevice() && stock}

                {/** Calibration */}
                {props.calibrationArea && props.calibrationArea.hasCalibrationOptions && (
                  <Calibration
                    labels={props.labels}
                    isInFixedCTA={props.isInFixedCTA}
                    calibrationDetails={props.calibrationArea.details}
                    productService={props.productService}
                    openDropdownCallback={(isOpen) =>
                      //included timeout(0 milliseconds) to avoid selectCallback being called (updates the component props based on query response) at the same time
                      //that the component dropdown's state is being updated
                      setTimeout(
                        () =>
                          dispatch({
                            type: AddToBasketCTAActionType.SetIsCalibrationDropdownOpen,
                            isOpen: isOpen,
                          }),
                        0
                      )
                    }
                    selectCallback={onCalibrationChange}
                  />
                )}

                {/**PPK Modal */}
                {props.ppkArea && props.ppkArea.isVisible && props.ppkArea.isEnabled && (
                  <PPK
                    ppkArea={props.ppkArea}
                    standardSsm={props.priceArea.ssm}
                    labels={props.labels}
                    stockCode={props.titleArea?.stockCode}
                    isInFixedCTA={props.isInFixedCTA}
                    openDropdownCallback={(isOpen) =>
                      //included timeout(0 milliseconds) to avoid selectCallback being called (updates the component props based on query response) at the same time
                      //that the component dropdown's state is being updated
                      setTimeout(
                        () =>
                          dispatch({
                            type: AddToBasketCTAActionType.SetIsPPKDropdownOpen,
                            isOpen: isOpen,
                          }),
                        0
                      )
                    }
                    selectCallback={props.onPackagingChange}
                  />
                )}

                {/* Commented out due to https://teams.microsoft.com/l/message/19:b6ac2b16374b4309b0929d55b40ff4a2@thread.skype/1719567060355?tenantId=7fb5fdc4-3cde-4e38-b51c-657035e941da&groupId=1d32de75-caa7-4cb3-827d-69680e8407dc&parentMessageId=1719567060355&teamName=RS%20EMD&channelName=General&createdTime=1719567060355*/}
                {/* 2azzccv */}
                {/* {!(props.stockArea?.statusCode === StockStatus.Backordered && props.stockArea?.availabilityDate === null) && ( */}
                <>
                  <div className={styles['cta-area']} data-verbolia-id='add-to-basket-cta-area'>
                    <Dropdown
                      onSearchValueChange={onSearchValueChange}
                      onFocus={() => {
                        //to reset list especially for mobile modal
                        if (state.isSearchingUnlistedQuantity) {
                          dispatch({
                            type: AddToBasketCTAActionType.SearchListedQuantity,
                          });
                        }
                      }}
                      defaultSelection={
                        props.priceArea.prices[0].quantity !== state.lastPropPricesFirstPriceQty
                          ? props.priceArea.prices[0].quantity
                          : state.defaultDropdownSelection
                      }
                      className={styles.qty}
                      isOpen={state.isDropdownOpen}
                      isSearchable={true}
                      customSearchHeader={mobileSearchHeader()}
                      customSearchHeaderClassName='search-header'
                      searchableInputType='number'
                      additionalInfo={`${getLabel(props.labels, 'available_in_multiples')} ${props.priceArea.ssm}`}
                      list={getDropDownValues()}
                      isValid={state.isValidQty}
                      errorMessage={state.errorMessage}
                      validationCallback={() => state.isValidQty}
                      selectOnClickOutside={true}
                      searchableStepValue={props.priceArea.ssm}
                      minNumericValue={props.priceArea.minimumOrderQuantity}
                      onChangeState={(isOpen: boolean) => props.onQtyDropdownClick?.(isOpen)}
                      selectCallback={changeQuantity as (key: string | number) => void}
                    />
                    <Button
                      text={getLabel(props.labels, 'add_to_basket')}
                      className={styles.add}
                      disabled={!state.isValidQty || state.isBusy}
                      onClick={onAddToBasketClick}
                    />
                  </div>
                  {!state.isValidQty && <p className={styles.error}>{state.errorMessage}</p>}
                  {props.isInBasket && <p className={styles['in-basket']}>{getLabel(props.labels, 'in_the_basket')}</p>}
                </>
                {/* )} */}
              </div>

              {/* Add to parts list*/}
              {props.allowAddToPartsList && (
                <Button
                  text={getLabel(props.labels, 'add_to_parts_list')}
                  buttonType='text'
                  icon='star-outline'
                  className={`${styles['add-parts']} ${showImportedByAddress ? styles['add-parts-with-imported-by-address'] : ''}`}
                  disabled={!state.isValidQty}
                  onClick={props.onAddToPartsListClick}
                  data-verbolia-id='add-parts'
                />
              )}

              {!props.isInFixedCTA && showImportedByAddress && (
                <div className={styles['imported-by-container']} data-verbolia-id='imported-by-container'>
                  <span className={styles['imported-by']}>{getLabel(props.labels, 'imported_by')}:</span>
                  <p
                    className={styles['imported-by-address']}
                    dangerouslySetInnerHTML={{
                      __html: getLabel(props.labels, 'imported_by_address'),
                    }}
                  />
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );

  function reducer(state: State, action: Action): State {
    switch (action.type) {
      case AddToBasketCTAActionType.SetPriceBreakIndex:
        return {
          ...state,
          selectedPriceBreakIndex: action.index,
        };
      case AddToBasketCTAActionType.IsBusy: {
        return {
          ...state,
          isBusy: action.isBusy,
          isDropdownOpen: action.isDropdownOpen ?? state.isDropdownOpen,
        };
      }
      case AddToBasketCTAActionType.SetUnlistedQuantityPrice: {
        let isValid = !(action.message && action.message !== '');
        const doesQtyMatchSearched = action.qty === state.quantity;

        const setNewPrices = !state.isDropdownOpen && action.price && action.qty === state.quantity && isValid;

        return {
          ...state,
          unlistedQuantityPrice: doesQtyMatchSearched ? action.price : undefined,
          isValidQty: doesQtyMatchSearched ? isValid : state.isValidQty,
          errorMessage: doesQtyMatchSearched ? action.message : state.errorMessage,
          defaultDropdownSelection: doesQtyMatchSearched
            ? isValid
              ? state.defaultDropdownSelection
              : undefined //else it defaults to set value
            : state.defaultDropdownSelection,

          totalPriceIncVat: setNewPrices ? (action.price!.prices[0]?.totalPrice as IPrice) : state.totalPriceIncVat,
          totalPriceExVat: setNewPrices ? (action.price!.pricesExVat[0]?.totalPrice as IPrice) : state.totalPriceExVat,
          dualPriceIncVat: setNewPrices ? (action.price!.priceDualCurrency as IPrice) : state.dualPriceIncVat,
          dualPriceExVat: setNewPrices ? (action.price!.priceExVatDualCurrency as IPrice) : state.dualPriceExVat,
        };
      }
      case AddToBasketCTAActionType.SearchUnlistedQuantity: {
        return {
          ...state,
          quantity: action.quantity,
          isValidQty: true, // temporarily set to true, pending query result
          isSearchingUnlistedQuantity: true,
          defaultDropdownSelection: undefined,
          unlistedQuantityPrice: undefined,
        };
      }
      case AddToBasketCTAActionType.SetInvalidUnlistedQty: {
        return {
          ...state,
          quantity: action.quantity,
          defaultDropdownSelection: undefined,
          unlistedQuantityPrice: undefined,
          isValidQty: false,
          errorMessage: props.priceArea.qtyErrors?.find((e) => e.errorCode === action.errorCode)?.errorMessage,
        };
      }
      case AddToBasketCTAActionType.SearchListedQuantity: {
        return {
          ...state,
          quantity: action.quantity ?? state.quantity,
          isDropdownOpen: action.isDropdownOpen !== undefined ? action.isDropdownOpen : state.isDropdownOpen,
          isSearchingUnlistedQuantity: false,
          unlistedQuantityPrice: undefined,
          defaultDropdownSelection: undefined, //else it defaults to set value
          isValidQty: action.quantity ? true : state.isValidQty,
          errorMessage: action.quantity ? '' : state.errorMessage,
        };
      }
      case AddToBasketCTAActionType.UpdateDefaultDropdownSelection: {
        let validationRes = Validation.validateQuantity(
          props.priceArea.minimumOrderQuantity,
          props.priceArea.ssm,
          props.priceArea.prices[0].quantity.toString()
        );

        return {
          ...state,
          quantity: props.priceArea.minimumOrderQuantity,
          defaultDropdownSelection: props.priceArea.prices[0].quantity,
          lastPropPricesFirstPriceQty: props.priceArea.prices[0].quantity,
          lastPropPricesFirstTotalPrice: props.priceArea.prices[0].totalPrice,
          isValidQty: validationRes.isValid,
          errorMessage: props.priceArea.qtyErrors?.find((e) => e.errorCode === validationRes.errorCode)?.errorMessage,
          isSearchingUnlistedQuantity: false,
          unlistedQuantityPrice: undefined,

          totalPriceIncVat: props.priceArea.prices[0]?.totalPrice,
          totalPriceExVat:
            props.priceArea.showExVat && props.priceArea.pricesExVat.length > 0 ? props.priceArea.pricesExVat[0]?.totalPrice : undefined,
          dualPriceIncVat: props.priceArea.priceDualCurrency ?? undefined,
          dualPriceExVat: props.priceArea.priceExVatDualCurrency ?? undefined,
        };
      }
      case AddToBasketCTAActionType.SetIsCalibrationDropdownOpen: {
        return {
          ...state,
          isCalibrationDropdownOpen: action.isOpen,
        };
      }
      case AddToBasketCTAActionType.SetIsPPKDropdownOpen: {
        return {
          ...state,
          isPPKDropdownOpen: action.isOpen,
        };
      }
      case AddToBasketCTAActionType.SetPrice: {
        return {
          ...state,
          quantity: action.qty,
          totalPriceIncVat: action.totalPriceIncVat ?? state.totalPriceIncVat,
          totalPriceExVat: action.totalPriceExVat ?? state.totalPriceExVat,
          dualPriceIncVat: action.dualPriceIncVat ?? state.dualPriceIncVat,
          dualPriceExVat: action.dualPriceExVat ?? state.dualPriceExVat,
        };
      }
      case AddToBasketCTAActionType.CalibrationChange: {
        const isListedQty =
          (incVat || !props.priceArea.showExVat ? props.priceArea.prices : props.priceArea.pricesExVat)
            .map((p) => p.quantity.toString())
            .filter((p) => p === state.quantity.toString().toLowerCase()).length > 0;

        return {
          ...state,
          isCalibrationChanging: action.isChanging,
          lastPropPricesFirstPriceQty: action.isChanging ? state.lastPropPricesFirstPriceQty : props.priceArea.prices[0].quantity,
          lastPropPricesFirstTotalPrice: action.isChanging ? state.lastPropPricesFirstTotalPrice : props.priceArea.prices[0].totalPrice,

          totalPriceIncVat:
            isListedQty && !action.isChanging
              ? props.priceArea.prices.filter((p) => p.quantity === state.quantity)[0].totalPrice
              : state.totalPriceIncVat,
          totalPriceExVat:
            isListedQty && !action.isChanging
              ? props.priceArea.showExVat
                ? props.priceArea.pricesExVat.filter((p) => p.quantity === state.quantity)[0].totalPrice
                : undefined
              : state.totalPriceExVat,
          dualPriceIncVat: isListedQty && !action.isChanging ? (props.priceArea.priceDualCurrency as IPrice) : state.dualPriceIncVat,
          dualPriceExVat: isListedQty && !action.isChanging ? (props.priceArea.priceExVatDualCurrency as IPrice) : state.dualPriceExVat,
        };
      }
    }
  }

  // update pricing if qty selected is from qty list
  function changeQuantity(quantity: number) {
    if (!state.isSearchingUnlistedQuantity) {
      props.onQtyChange && props.onQtyChange(quantity);

      const totalPriceIncVat = props.priceArea.prices.filter((p) => p.quantity === quantity)[0].totalPrice;
      const totalPriceExVat = props.priceArea.showExVat
        ? props.priceArea.pricesExVat.filter((p) => p.quantity === quantity)[0].totalPrice
        : undefined;
      const dualPriceIncVat = props.priceArea.priceDualCurrency as IPrice;
      const dualPriceExVat = props.priceArea.priceExVatDualCurrency as IPrice;

      dispatch({
        type: AddToBasketCTAActionType.SetPrice,
        qty: quantity,
        totalPriceIncVat: totalPriceIncVat,
        totalPriceExVat: totalPriceExVat,
        dualPriceIncVat: dualPriceIncVat,
        dualPriceExVat: dualPriceExVat,
      });
    }
  }

  function getThumbnail() {
    if (props.mediaArea) {
      let mediaItem = getPrimaryImageUrl(props.mediaArea, undefined, 'thumb');
      if (mediaItem)
        return (
          <div className={styles['modal-thumb-container']}>
            <img className={styles.thumb} id={`add-thumbnail-${mediaItem.id}`} src={mediaItem.url} alt={`thumbnail-${mediaItem.name}`} />
          </div>
        );
    }
  }

  function getPriceLine() {
    let priceBreakIncVAT = props.priceArea.priceBreaks[state.selectedPriceBreakIndex ?? 0];
    let priceBreakExVAT = props.priceArea.priceBreaksExVat[state.selectedPriceBreakIndex ?? 0];
    let equalOrGreaterThanEightChar = false;

    //check if prices exceed or are equal to 8 characters
    equalOrGreaterThanEightChar =
      checkPriceLengthGreaterThanEightChar(priceBreakIncVAT?.unitPrice) || checkPriceLengthGreaterThanEightChar(state.totalPriceIncVat);

    if (props.priceArea.showExVat && !incVat && !equalOrGreaterThanEightChar) {
      equalOrGreaterThanEightChar =
        checkPriceLengthGreaterThanEightChar(priceBreakExVAT?.unitPrice) || checkPriceLengthGreaterThanEightChar(state.totalPriceExVat);
    }

    return (
      <div className={`${styles['price-line']} ${equalOrGreaterThanEightChar ? styles['greater-equal-eight-char'] : ''}`}>
        {/* selected Price */}
        {getPriceBlock(
          true,
          incVat || !props.priceArea.showExVat ? state.totalPriceIncVat : state.totalPriceExVat,
          (incVat || !props.priceArea.showExVat ? priceBreakIncVAT : priceBreakExVAT)?.unitPrice,
          incVat ? state.dualPriceIncVat : state.dualPriceExVat,
          incVat
        )}
        {/* other Price, if not POA */}
        {props.priceArea.showExVat &&
          getPriceBlock(
            false,
            !incVat ? state.totalPriceIncVat : state.totalPriceExVat,
            (!incVat ? priceBreakIncVAT : priceBreakExVAT)?.unitPrice,
            !incVat ? state.dualPriceIncVat : state.dualPriceExVat,
            !incVat
          )}
      </div>
    );
  }

  function checkPriceLengthGreaterThanEightChar(price: IPrice | undefined): boolean {
    return (
      ((price?.priceBefore && price?.priceBefore?.displayValue.length >= 8) ||
        (price?.priceAfter && price?.priceAfter?.displayValue.length >= 8)) ??
      false
    );
  }

  function getPriceBlock(
    selected: boolean,
    price: IPrice | undefined,
    unitPrice: IPrice | undefined,
    dualCurrency: IPrice | undefined,
    incVat: boolean
  ) {
    return (
      <div
        className={`${styles['price-block']} ${selected ? styles['selected'] : ''} ${dualCurrency ? styles['dual-price-block-euro'] : ''}`}>
        <div className={`${styles.price} ${dualCurrency ? styles['dual-price'] : styles['single-price']}`}>
          <div className={styles['unit-price-container']}>
            <p className={styles['unit-price']}>{price?.priceAfter?.displayValue}</p>
            {/**Croatia dual currency */}
            {dualCurrency && <p className={styles['unit-dual-price']}>{dualCurrency.priceAfter?.displayValue}</p>}
          </div>
          {price?.priceAfter?.value !== price?.priceBefore?.value && (
            <div className={styles.discount}>
              <p>{price?.priceBefore?.displayValue}</p>
              {dualCurrency && <p>{dualCurrency?.priceBefore?.displayValue}</p>}
            </div>
          )}
        </div>
        {/* hide extra info if POA */}
        {unitPrice?.priceAfter?.value !== 0 && (
          <p className={styles.container}>
            {unitPrice?.priceAfter?.displayValue} {props.priceArea.container}{' '}
            {getLabel(props.labels, incVat ? 'inc_vat_brackets' : 'ex_vat_brackets')}
          </p>
        )}
      </div>
    );
  }

  function mobileSearchHeader() {
    return (
      <div className={styles['modal-search-header']}>
        <span className={styles.title}>{getLabel(props.labels, 'select_quantity')}</span>
        <div className={styles.info}>
          <div className={styles['header-product-info']}>
            {getThumbnail()}
            <div className={styles.details}>
              <span>{props.titleArea?.title}</span>
              {getPriceLine()}
            </div>
          </div>
          <div className={styles.cta}>
            <Button
              text={getLabel(props.labels, 'add_to_basket')}
              className={styles.add}
              disabled={!state.isValidQty || state.isBusy}
              onClick={onAddToBasketClick}
            />
          </div>
        </div>
      </div>
    );
  }

  function onAddToBasketClick() {
    if (state.isValidQty) {
      dispatch({ type: AddToBasketCTAActionType.IsBusy, isBusy: true });

      props
        .onAddToBasketClick(state.quantity)
        .then(() => dispatch({ type: AddToBasketCTAActionType.IsBusy, isBusy: false, isDropdownOpen: false }));
    }
  }

  function onCalibrationChange(stockCode: string) {
    props.onCalibrationChange(stockCode);
    dispatch({ type: AddToBasketCTAActionType.CalibrationChange, isChanging: true });
  }
};
